<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-3">
      <div
        v-for="(investor, index) in investors"
        :key="index"
        class="flex flex-col gap-3 rounded-ds-lg border p-4 shadow-ds-sm"
        :class="isInvestorResolved(investor) ? 'border-border' : 'border-warning'"
      >
        <div class="flex items-center gap-2.5">
          <span
            class="flex-1 text-[10px] font-bold uppercase tracking-ds-caps text-muted-foreground"
          >
            Investor {{ index + 1 }}
          </span>
          <DSButton
            v-if="investors.length > 1"
            type="button"
            variant="ghost"
            size="icon-sm"
            class="shrink-0 text-muted-foreground hover:bg-destructive-subtle hover:text-destructive-subtle-foreground -mt-1"
            aria-label="Remove this investor"
            @click="removeInvestor(index)"
          >
            <Trash2 />
          </DSButton>
        </div>

        <!-- Existing account, or a new one to create — same either/or the ownership step asks. -->
        <div
          v-if="investor.accountMode === WizardAccountMode.EXISTING"
          class="min-w-0"
        >
          <DSFieldLabel
            label="Entity or account"
            class="mb-1.5"
            :mark="investorMark(index)"
          />
          <AccountPicker
            :model-value="investor.accountId"
            :accounts="accountOptions"
            placeholder="Find or create new"
            empty-label="Nothing to pick — create a new account instead."
            @select="account => handlePick(index, account)"
          >
            <template #note>
              Person, trust, company, grouping account and investment account assets only.
              Feed-maintained accounts are excluded.
            </template>
          </AccountPicker>
        </div>
        <div
          v-else
          class="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <div class="min-w-0">
            <DSFieldLabel
              label="Account"
              class="mb-1.5"
              :mark="investorMark(index)"
            />
            <DSInput
              :model-value="investor.accountName"
              placeholder="Ex. John Smith Trust"
              @update:model-value="value => setField(index, 'accountName', String(value ?? ''))"
            />
          </div>
          <div class="min-w-0">
            <DSFieldLabel
              label="Type"
              class="mb-1.5"
              :mark="investorMark(index)"
            />
            <DSSelect
              :model-value="investor.accountType"
              @update:model-value="value => setField(index, 'accountType', String(value))"
            >
              <DSSelectTrigger>
                <DSSelectValue placeholder="Choose one" />
              </DSSelectTrigger>
              <DSSelectContent>
                <DSSelectItem
                  v-for="type in accountTypes"
                  :key="type"
                  :value="type"
                >
                  {{ type }}
                </DSSelectItem>
              </DSSelectContent>
            </DSSelect>
          </div>
        </div>

        <button
          type="button"
          class="self-start text-xs font-bold text-brand hover:text-brand/80 cursor-pointer"
          @click="toggleMode(index)"
        >
          {{ investor.accountMode === WizardAccountMode.EXISTING
            ? 'Create a new account instead'
            : 'Use an existing account instead' }}
        </button>
      </div>
    </div>

    <DSButton
      type="button"
      variant="secondary"
      @click="addInvestor"
    >
      <Plus class="size-4" />
      Add another investor
    </DSButton>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Plus, Trash2 } from '@lucide/vue'

import type { FieldLabelMark } from '@ds'
import {
  DSButton,
  DSFieldLabel,
  DSInput,
  DSSelect,
  DSSelectContent,
  DSSelectItem,
  DSSelectTrigger,
  DSSelectValue,
} from '@ds'

import AccountPicker from '@/entities/assets/components/AccountPicker.vue'
import { AccountOption } from '@/entities/assets/utils/types'
import { AssetWizardAccount, AssetWizardEntity } from '@/store/ai/assetWizard/utils/types'

import { isInvestorResolved } from '../utils/schema'
import { emptyInvestor } from '../utils/helpers'
import { useWizardOptions } from '../utils/options'
import {
  WizardAccountMode,
  WizardCashFlow,
  WizardFieldDef,
  WizardFieldOrigin,
  WizardInvestor,
} from '../utils/types'

/*
 * The one step the generic field grid cannot express: more than one entity
 * each independently holding the same position — each its own asset-and-
 * transaction bundle (see PrivateFund/create.ts), for the full committed
 * amount, never a share of it. private_fund's own — private_security used
 * to share this too, before it moved to the split model instead (one cost
 * divided across entities, InvestorAllocationBar and all); see
 * InvestorsStep.vue.
 *
 * InvestorsStep.vue is the sibling this one used to be — same account
 * picking, same remove/add rows — minus Share and Percent entirely: there
 * is no commitment to divide here, so there is nothing for those two fields
 * to hold, and no InvestorAllocationBar or "Split evenly" button to go with
 * them.
 *
 * Real account picking only, no "Ask AI" here — the two suggestion assistants
 * both answer about a single account, and this step asks about several at
 * once. A manual entry opens on one empty row.
 */
const { form, transactionType, entities: suggested } = defineProps<{
  form: any
  fields: WizardFieldDef[]
  origins: Record<string, WizardFieldOrigin>
  /** Which account types may hold a fund position — the flow's own transaction. */
  transactionType: string
  /** Declared so Wizard.vue can hand every bespoke step the same bag. Unused: this step asks about several accounts, not one the flow read. */
  flow?: string
  ownerAccount?: AssetWizardAccount
  /**
   * What the read suggested, if it suggested anything — one entry per entity
   * named in the sentence, seeded into `values.investors` by `seedEntities`
   * before this step ever mounts. Only its length matters here, for
   * `investorMark` below; the rows themselves are read off the form.
   */
  entities?: AssetWizardEntity[]
  sentence?: string
}>()

const { accountsOf, accountTypesFor } = useWizardOptions()

const accountOptions = computed(() => accountsOf(transactionType))
const accountTypes = computed(() => accountTypesFor(transactionType))

/*
 * Read off the form rather than held here: the step is remounted every time
 * the user walks back into it, and investors kept in component state would
 * be lost the moment they left.
 */
const investorsSelector = form.useSelector((state: any): WizardInvestor[] => state.values.investors ?? [])
const investors = computed<WizardInvestor[]>(() => investorsSelector.value)

/**
 * Sparkle the rows the read actually filled, same rule InvestorsStep.vue's
 * own `investorMark` follows — `ai` wins over `required` regardless of
 * whether the row has since been edited, and every other row is `required`:
 * the account is never optional here, even without a share to size it by.
 */
const investorMark = (index: number): FieldLabelMark | undefined =>
  index < (suggested?.length ?? 0) ? 'ai' : 'required'

const setInvestors = (next: WizardInvestor[]) => form.setFieldValue('investors', next)

const updateInvestor = (index: number, patch: Partial<WizardInvestor>) => {
  setInvestors(investors.value.map((investor: WizardInvestor, i: number) =>
    i === index ? { ...investor, ...patch } : investor))
}

/** Picking copies the account's own name and type in beside its id. */
const handlePick = (index: number, account: AccountOption) => {
  updateInvestor(index, { accountId: account.id, accountName: account.name, accountType: account.type })
}

const setField = (index: number, key: 'accountName' | 'accountType', value: string) => {
  updateInvestor(index, { [key]: value })
}

/*
 * Switching modes clears the account fields. The alternative — keeping the
 * name of an account the user has just stopped pointing at — leaves a value
 * nobody chose in a field the schema is about to require differently.
 */
const toggleMode = (index: number) => {
  const nextMode = investors.value[index].accountMode === WizardAccountMode.EXISTING
    ? WizardAccountMode.NEW
    : WizardAccountMode.EXISTING
  updateInvestor(index, { accountMode: nextMode, accountId: '', accountName: '', accountType: '' })
}

const addInvestor = () => setInvestors([...investors.value, emptyInvestor()])

/**
 * A cash flow keyed to the row being removed — see WizardCashFlow — would
 * otherwise silently point at whichever investor slides into its old
 * position. Dropped rather than reassigned: which entity a logged call
 * belonged to is not something removing a different row should guess at.
 * Read off the form directly rather than through a selector: this only ever
 * runs once, at the moment of the click, not on every render.
 */
const reindexCashFlows = (removedIndex: number) => {
  const cashFlows: WizardCashFlow[] = form.state.values.cashFlows ?? []
  if (!cashFlows.length) return

  form.setFieldValue(
    'cashFlows',
    cashFlows
      .filter(flow => flow.investorIndex !== removedIndex)
      .map(flow => (flow.investorIndex > removedIndex
        ? { ...flow, investorIndex: flow.investorIndex - 1 }
        : flow)),
  )
}

/** The last entity is not removable — the fund needs at least one holder. */
const removeInvestor = (index: number) => {
  if (investors.value.length <= 1) return
  setInvestors(investors.value.filter((_: WizardInvestor, i: number) => i !== index))
  reindexCashFlows(index)
}
</script>
